/**
 * Database API client
 * Provides typed database operations using Supabase
 */

import { getSupabaseClient } from './supabase'
import type { Database } from './types/database'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Generates a secure random password
 */
function generateSecurePassword(length: number = 12): string {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
  const values = new Uint32Array(length)
  crypto.getRandomValues(values)
  return Array.from(values, x => charset[x % charset.length]).join('')
}

/**
 * Generates a secure random API key
 */
function generateApiKey(): string {
  const prefix = 'biz_'
  const randomBytes = new Uint8Array(32)
  crypto.getRandomValues(randomBytes)
  const randomString = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return prefix + randomString
}

/**
 * Hashes a password using bcrypt via Supabase Edge Function
 * Falls back to SHA-256 if Edge Function is not available (backward compatibility)
 * 
 * Updated: 2024-12-19 - Task 2.1
 * - Migrated from client-side SHA-256 to server-side bcrypt via Edge Functions
 * 
 * @param password - Plain text password to hash
 * @returns Hashed password (bcrypt or SHA-256)
 */
async function hashPassword(password: string): Promise<string> {
  try {
    // Try Edge Function first (preferred method)
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL
    const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY

    if (supabaseUrl && supabaseAnonKey) {
      const response = await fetch(`${supabaseUrl}/functions/v1/hash-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({ password })
      })

      if (response.ok) {
        const { hashedPassword } = await response.json()
        if (hashedPassword) {
          return hashedPassword
        }
      }
    }
  } catch (error) {
    console.warn('[DB] Edge Function hash-password failed, falling back to SHA-256:', error)
  }

  // Fallback to SHA-256 (for backward compatibility during migration)
  const encoder = new TextEncoder()
  const data = encoder.encode(password)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

/**
 * App types for URL generation
 */
type AppType = 'tenant-admin' | 'storefront' | 'platform-admin' | 'landing'

/**
 * Gets the origin (protocol + hostname + port) for a specific app
 * Uses environment variables if available, otherwise falls back to port mapping in dev
 * 
 * @param app - The app type ('tenant-admin', 'storefront', 'platform-admin', 'landing')
 * @returns The origin URL for the app
 */
function getAppOrigin(app: AppType): string {
  if (typeof window === 'undefined') {
    // Server-side: use default production domain
    return 'https://bizora.com'
  }
  
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  
  // Check for environment variables first
  const env = (import.meta as any).env || {}
  
  if (app === 'tenant-admin') {
    const envOrigin = env.VITE_TENANT_ADMIN_ORIGIN
    if (envOrigin) return envOrigin
    if (isLocalhost) return `${protocol}//${hostname}:3003`
  } else if (app === 'storefront') {
    const envOrigin = env.VITE_STOREFRONT_ORIGIN
    if (envOrigin) return envOrigin
    if (isLocalhost) return `${protocol}//${hostname}:3004`
  } else if (app === 'platform-admin') {
    const envOrigin = env.VITE_PLATFORM_ADMIN_ORIGIN
    if (envOrigin) return envOrigin
    if (isLocalhost) return `${protocol}//${hostname}:3002`
  } else if (app === 'landing') {
    const envOrigin = env.VITE_LANDING_ORIGIN
    if (envOrigin) return envOrigin
    if (isLocalhost) return `${protocol}//${hostname}:3001`
  }
  
  // Production fallback: use current domain
  return `${protocol}//${hostname}`
}

/**
 * Generates tenant admin portal URL based on slug
 * Format: http://localhost:3003/t/{tenant-slug}/admin/
 * Architecture spec: /t/{tenant-slug}/admin/
 * 
 * @param tenantSlug - The tenant slug
 * @returns The admin portal URL for the tenant
 */
export function getTenantAdminUrl(tenantSlug: string): string {
  const origin = getAppOrigin('tenant-admin')
  return `${origin}/t/${tenantSlug}/admin/`
}

/**
 * Generates storefront (customer website) URL based on slug
 * Format: http://localhost:3004/t/{tenant-slug}/
 * Architecture spec: /t/{tenant-slug}/
 * 
 * @param tenantSlug - The tenant slug
 * @returns The storefront URL for the tenant
 */
export function getStorefrontUrl(tenantSlug: string): string {
  const origin = getAppOrigin('storefront')
  return `${origin}/t/${tenantSlug}/`
}

/**
 * Generates tenant customer website URL based on slug
 * Format: http://localhost:3004/t/{tenant-slug}/
 * Architecture spec: /t/{tenant-slug}/
 * 
 * @deprecated Use getStorefrontUrl() instead for clarity
 */
export function generateCustomerWebsiteUrl(slug: string): string {
  return getStorefrontUrl(slug)
}

/**
 * Generates tenant admin portal URL based on slug
 * Format: http://localhost:3003/t/{tenant-slug}/admin/
 * Architecture spec: /t/{tenant-slug}/admin/
 * 
 * @deprecated Use getTenantAdminUrl() instead for clarity
 */
export function generateAdminPortalUrl(slug: string): string {
  return getTenantAdminUrl(slug)
}

/**
 * Permission checking with owner override
 * 
 * Checks if a user has permission to perform an action, with special handling for owners:
 * - If userId === tenant.owner_user_id OR role includes "owner" -> allow all permissions
 * - Otherwise, evaluate role_permissions normally
 * 
 * @param userId - The user ID to check
 * @param role - The user's role (e.g., 'owner', 'manager', 'staff')
 * @param permission - The permission to check (e.g., 'orders.view', 'catalog.manage')
 * @param tenant - Optional tenant object with owner_user_id field
 * @param rolePermissions - Optional map of role to permissions array. If not provided, uses default permissions
 * @returns true if user has permission, false otherwise
 */
export function checkPermission(
  userId: string | null | undefined,
  role: string,
  permission: string,
  tenant?: { owner_user_id?: string | null } | null,
  rolePermissions?: Record<string, string[]>
): boolean {
  // Normalize role to lowercase
  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : role
  
  // Check if user is the tenant owner by user ID
  if (userId && tenant?.owner_user_id && userId === tenant.owner_user_id) {
    return true // Owner has all permissions
  }
  
  // Check if role includes "owner"
  if (normalizedRole === 'owner' || normalizedRole.includes('owner')) {
    return true // Owner role has all permissions
  }
  
  // Otherwise, evaluate role_permissions normally
  if (rolePermissions) {
    const permissions = rolePermissions[normalizedRole]
    return permissions?.includes(permission) ?? false
  }
  
  // Default role permissions if not provided
  const defaultRolePermissions: Record<string, string[]> = {
    owner: [
      'orders.view',
      'orders.create',
      'orders.update',
      'catalog.manage',
      'customers.manage',
      'billing.view',
      'settings.manage',
      'users.manage'
    ],
    manager: [
      'orders.view',
      'orders.create',
      'orders.update',
      'catalog.manage',
      'customers.manage',
      'billing.view'
    ],
    staff: [
      'orders.view'
    ]
  }
  
  const permissions = defaultRolePermissions[normalizedRole]
  return permissions?.includes(permission) ?? false
}

/**
 * Get a typed Supabase client for database operations
 */
export function getDb(): SupabaseClient<Database> {
  try {
    return getSupabaseClient()
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    console.error('[getDb] Failed to initialize Supabase client:', errorMessage)
    console.error('[getDb] Make sure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set in .env file')
    throw new Error(`Supabase client initialization failed: ${errorMessage}`)
  }
}

/**
 * Database query helpers with tenant filtering
 * These functions automatically filter by tenant_id when available
 */

/**
 * Fetches catalog items for a tenant
 */
export async function fetchCatalog(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch catalog: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches orders for a tenant
 */
export async function fetchOrders(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('orders')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch orders: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches customers for a tenant
 */
export async function fetchCustomers(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('customers')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch customers: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches a single order by ID
 */
export async function fetchOrderById(tenantId: string, orderId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('orders')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('id', orderId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch order: ${error.message}`)
  }

  return data
}

/**
 * Fetches a single product by ID
 */
export async function fetchProductById(tenantId: string, productId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('id', productId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch product: ${error.message}`)
  }

  return data
}

/**
 * Creates a new order
 */
export async function createOrder(tenantId: string, orderData: any) {
  const db = getDb()
  const { data, error } = await db
    .from('orders')
    .insert({
      ...orderData,
      tenant_id: tenantId
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create order: ${error.message}`)
  }

  return data
}

/**
 * Updates an order
 */
export async function updateOrder(tenantId: string, orderId: string, updates: any) {
  const db = getDb()
  const { data, error } = await db
    .from('orders')
    .update(updates)
    .eq('tenant_id', tenantId)
    .eq('id', orderId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update order: ${error.message}`)
  }

  return data
}

/**
 * Creates a new product in catalog
 */
export async function createProduct(tenantId: string, productData: any) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog')
    .insert({
      ...productData,
      tenant_id: tenantId
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create product: ${error.message}`)
  }

  return data
}

/**
 * Updates a product in catalog
 */
export async function updateProduct(tenantId: string, productId: string, updates: any) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog')
    .update(updates)
    .eq('tenant_id', tenantId)
    .eq('id', productId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update product: ${error.message}`)
  }

  return data
}

/**
 * Deletes a product from catalog
 */
export async function deleteProduct(tenantId: string, productId: string) {
  const db = getDb()
  const { error } = await db
    .from('catalog')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('id', productId)

  if (error) {
    throw new Error(`Failed to delete product: ${error.message}`)
  }
}

/**
 * Tenant Management Functions
 * These functions are used by the platform admin to manage tenants
 */

/**
 * Fetches all tenants (platform admin only)
 */
export async function fetchTenants() {
  try {
    const db = getDb()
    const { data, error } = await db
      .from('tenants')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('[fetchTenants] Supabase error:', error)
      throw new Error(`Failed to fetch tenants: ${error.message}`)
    }

    return data || []
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('[fetchTenants] Network error - check Supabase connection:')
      console.error('  - Verify VITE_SUPABASE_URL is correct in .env file')
      console.error('  - Verify VITE_SUPABASE_ANON_KEY is correct in .env file')
      console.error('  - Check if Supabase project is active (not paused)')
      console.error('  - Check browser console Network tab for detailed error')
      throw new Error('Failed to connect to Supabase. Please check your .env configuration and ensure the Supabase project is active.')
    }
    throw error
  }
}

/**
 * Fetches a single tenant by ID
 */
export async function fetchTenantById(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .select('*')
    .eq('id', tenantId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch tenant: ${error.message}`)
  }

  // Log access information (for debugging)
  if (import.meta.env.DEV && data) {
    console.log('[fetchTenantById] Retrieved access information:', {
      admin_username: data.admin_username,
      admin_portal_url: data.admin_portal_url,
      customer_website_url: data.customer_website_url,
      api_key: data.api_key ? `${data.api_key.substring(0, 8)}...` : 'null',
      has_password_hash: !!data.admin_password_hash
    })
  }

  return data
}

/**
 * Fetches a tenant by slug
 */
export async function fetchTenantBySlug(slug: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()

  // Handle 406 (Not Acceptable) or other RLS/API errors gracefully
  if (error) {
    // If it's a "not found" type error, return null instead of throwing
    if (error.code === 'PGRST116' || error.message.includes('No rows') || error.message.includes('Cannot coerce')) {
      return null
    }
    // For 406 or other API errors, return null silently (RLS blocking is expected for some tenants)
    if (error.message.includes('406') || error.code === '406' || error.message.includes('Not Acceptable')) {
      // Don't log 406 errors - they're expected when RLS blocks access or tenant doesn't exist
      return null
    }
    // For other errors, still throw
    throw new Error(`Failed to fetch tenant: ${error.message}`)
  }

  return data
}

/**
 * ============================================================================
 * TENANT ONBOARDING & STATUS HELPERS
 * ============================================================================
 */

/**
 * Tenant onboarding step status
 */
export interface OnboardingStepStatus {
  step: string
  completed: boolean
  required: boolean
}

/**
 * Tenant onboarding status
 */
export interface TenantOnboardingStatus {
  isComplete: boolean
  isPublished: boolean
  steps: OnboardingStepStatus[]
  completedSteps: number
  totalSteps: number
  progress: number // 0-100
}

/**
 * Checks if tenant has completed business information setup
 */
export function hasBusinessInfoComplete(tenant: any): boolean {
  return !!(
    tenant?.name &&
    tenant?.email &&
    tenant?.phone &&
    tenant?.vertical
  )
}

/**
 * Checks if tenant has selected a theme
 */
export function hasThemeSelected(tenant: any): boolean {
  return !!(tenant?.theme || tenant?.theme_id)
}

/**
 * Checks if tenant has catalog setup
 */
export async function hasCatalogSetup(tenantId: string): Promise<boolean> {
  try {
    const catalog = await fetchCatalog(tenantId)
    return catalog && catalog.length > 0
  } catch {
    return false
  }
}

/**
 * Checks if tenant has payment/tax settings configured
 * Validates that at least one payment method is enabled:
 * - COD enabled, OR
 * - Payment gateway is configured (gateway set and gateway_configured flag is true if present)
 */
export async function hasPaymentsConfigured(tenantId: string): Promise<boolean> {
  try {
    const paymentsConfig = await getSetting('payments_config', tenantId)
    
    if (!paymentsConfig || typeof paymentsConfig !== 'object') {
      return false
    }

    const config = paymentsConfig as any

    // Check if COD is enabled
    if (config.cod_enabled === true) {
      return true
    }

    // If COD is not enabled, check if payment gateway is configured
    // Gateway is considered configured if:
    // 1. payment_gateway is set and not empty
    // 2. gateway_configured flag is true (if present)
    const hasGateway = config.payment_gateway && 
                       typeof config.payment_gateway === 'string' && 
                       config.payment_gateway.trim() !== '' &&
                       config.payment_gateway !== 'none'

    if (hasGateway) {
      // If gateway_configured flag exists, check it
      if (config.gateway_configured !== undefined) {
        return config.gateway_configured === true
      }
      // If flag doesn't exist, assume gateway selection means it's configured
      // (In production, you'd want to check actual API keys from integrations table)
      return true
    }

    return false
  } catch (error) {
    console.error('[hasPaymentsConfigured] Error checking payment configuration:', error)
    return false
  }
}

/**
 * Checks if tenant has shipping settings configured
 * Validates that at least one shipping method is enabled and properly configured:
 * - shipping_enabled: true with default_rate > 0, OR
 * - pickup_enabled: true, OR
 * - local_delivery_enabled: true
 */
export async function hasShippingConfigured(tenantId: string): Promise<boolean> {
  try {
    const shippingConfig = await getSetting('shipping_config', tenantId)
    
    if (!shippingConfig || typeof shippingConfig !== 'object') {
      return false
    }

    const config = shippingConfig as any

    // Check if pickup is enabled (no additional config needed)
    if (config.pickup_enabled === true) {
      return true
    }

    // Check if local delivery is enabled (no additional config needed)
    if (config.local_delivery_enabled === true) {
      return true
    }

    // Check if shipping is enabled with valid rate configuration
    if (config.shipping_enabled === true) {
      // Validate that default_rate is set and greater than 0
      const defaultRate = config.default_rate
      if (defaultRate !== undefined && defaultRate !== null) {
        const rate = typeof defaultRate === 'number' ? defaultRate : parseFloat(defaultRate)
        if (!isNaN(rate) && rate >= 0) {
          return true
        }
      }
    }

    return false
  } catch (error) {
    console.error('[hasShippingConfigured] Error checking shipping configuration:', error)
    return false
  }
}

/**
 * Gets tenant onboarding status
 * Derives onboarding completion from tenant data without changing API payloads
 */
export async function getTenantOnboardingStatus(tenant: any): Promise<TenantOnboardingStatus> {
  if (!tenant || !tenant.id) {
    return {
      isComplete: false,
      isPublished: false,
      steps: [],
      completedSteps: 0,
      totalSteps: 0,
      progress: 0
    }
  }

  // Define onboarding steps
  const steps: OnboardingStepStatus[] = [
    {
      step: 'business',
      completed: hasBusinessInfoComplete(tenant),
      required: true
    },
    {
      step: 'theme',
      completed: hasThemeSelected(tenant),
      required: true
    },
    {
      step: 'catalog',
      completed: await hasCatalogSetup(tenant.id),
      required: true
    },
    {
      step: 'payments',
      completed: await hasPaymentsConfigured(tenant.id),
      required: true
    },
    {
      step: 'shipping',
      completed: await hasShippingConfigured(tenant.id),
      required: true
    },
    {
      step: 'review',
      completed: false, // Review step is always incomplete until explicitly marked
      required: true
    }
  ]

  const completedSteps = steps.filter(s => s.completed).length
  const totalSteps = steps.length
  const requiredSteps = steps.filter(s => s.required)
  const completedRequiredSteps = requiredSteps.filter(s => s.completed).length
  const isComplete = completedRequiredSteps === requiredSteps.length

  // Check if tenant is published (status === 'active', all required steps complete, AND published_at is set)
  const isPublished = tenant.status === 'active' && isComplete && !!tenant.published_at

  return {
    isComplete,
    isPublished,
    steps,
    completedSteps,
    totalSteps,
    progress: totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0
  }
}

/**
 * Checks if tenant is published (publicly accessible)
 * Tenant is published if status is 'active' AND published_at is set
 */
export function isTenantPublished(tenant: any): boolean {
  return tenant?.status === 'active' && !!tenant?.published_at
}

/**
 * Gets tenant status summary
 */
export interface TenantStatusSummary {
  isPublished: boolean
  hasTheme: boolean
  hasCatalog: boolean
  onboardingComplete: boolean
}

export async function getTenantStatusSummary(tenant: any): Promise<TenantStatusSummary> {
  if (!tenant || !tenant.id) {
    return {
      isPublished: false,
      hasTheme: false,
      hasCatalog: false,
      onboardingComplete: false
    }
  }

  const onboarding = await getTenantOnboardingStatus(tenant)
  const hasCatalog = await hasCatalogSetup(tenant.id)

  return {
    isPublished: onboarding.isPublished,
    hasTheme: hasThemeSelected(tenant),
    hasCatalog,
    onboardingComplete: onboarding.isComplete
  }
}

/**
 * Tenant setup state with detailed onboarding flags
 */
export interface TenantSetupState {
  isPublished: boolean
  hasTheme: boolean
  requiredCompleteCount: number
  requiredTotal: number
  missingRequiredSteps: string[]
  nextStep: string | null
  statusLabel: 'CREATED' | 'ONBOARDING' | 'READY_TO_PUBLISH' | 'PUBLISHED' | 'SUSPENDED'
  onboardingFlags: {
    business_details_completed: boolean
    theme_selected: boolean
    catalog_ready: boolean
    payments_configured: boolean
    shipping_configured: boolean
    checkout_configured: boolean
    branding_completed: boolean
    staff_invited: boolean
    policies_added: boolean
    seo_configured: boolean
    analytics_connected: boolean
  }
}

/**
 * Gets comprehensive tenant setup state
 * Derives all onboarding flags from existing tenant data
 */
export async function getTenantSetupState(tenant: any): Promise<TenantSetupState> {
  if (!tenant || !tenant.id) {
    return {
      isPublished: false,
      hasTheme: false,
      requiredCompleteCount: 0,
      requiredTotal: 5,
      missingRequiredSteps: ['business', 'theme', 'catalog', 'payments', 'shipping'],
      nextStep: 'business',
      onboardingFlags: {
        business_details_completed: false,
        theme_selected: false,
        catalog_ready: false,
        payments_configured: false,
        shipping_configured: false,
        branding_completed: false,
        staff_invited: false,
        policies_added: false
      }
    }
  }

  // Check required flags
  const business_details_completed = hasBusinessInfoComplete(tenant)
  const theme_selected = hasThemeSelected(tenant)
  const catalog_ready = await hasCatalogSetup(tenant.id)
  const payments_configured = await hasPaymentsConfigured(tenant.id)
  const shipping_configured = await hasShippingConfigured(tenant.id)
  const checkout_configured = await hasCheckoutConfigured(tenant.id)

  // Optional flags (can be derived from settings or other tables)
  const branding_completed = !!(tenant.logo || (tenant as any).branding?.logoUrl)
  const staffCount = await checkStaffCount(tenant.id)
  const staff_invited = staffCount > 1 // More than just the owner
  const policies_added = await checkPolicies(tenant.id)
  
  // Check SEO and analytics (from settings)
  const db = getDb()
  let seo_configured = false
  let analytics_connected = false
  
  try {
    const { data: seoSettings } = await db
      .from('settings_values')
      .select('id')
      .eq('tenant_id', tenant.id)
      .in('setting_key', ['seo_title', 'seo_description', 'meta_tags'])
      .limit(1)
    seo_configured = !!(seoSettings && seoSettings.length > 0)
  } catch {
    seo_configured = false
  }
  
  try {
    const { data: analyticsSettings } = await db
      .from('settings_values')
      .select('id')
      .eq('tenant_id', tenant.id)
      .eq('setting_key', 'analytics_connected')
      .limit(1)
    analytics_connected = !!(analyticsSettings && analyticsSettings.length > 0)
  } catch {
    analytics_connected = false
  }

  const requiredFlags = [
    { key: 'business', completed: business_details_completed },
    { key: 'theme', completed: theme_selected },
    { key: 'catalog', completed: catalog_ready },
    { key: 'payments', completed: payments_configured },
    { key: 'shipping', completed: shipping_configured },
    { key: 'checkout', completed: checkout_configured }
  ]

  const completedRequired = requiredFlags.filter(f => f.completed)
  const missingRequiredSteps = requiredFlags.filter(f => !f.completed).map(f => f.key)
  const nextStep = missingRequiredSteps[0] || null

  // Check if published (status === 'active', all required complete, AND published_at is set)
  const allRequiredComplete = completedRequired.length === requiredFlags.length
  const isPublished = tenant.status === 'active' && allRequiredComplete && !!tenant.published_at

  // Determine status label
  let statusLabel: 'CREATED' | 'ONBOARDING' | 'READY_TO_PUBLISH' | 'PUBLISHED' | 'SUSPENDED'
  if (tenant.status === 'suspended') {
    statusLabel = 'SUSPENDED'
  } else if (isPublished) {
    statusLabel = 'PUBLISHED'
  } else if (allRequiredComplete) {
    statusLabel = 'READY_TO_PUBLISH'
  } else if (completedRequired.length > 0) {
    statusLabel = 'ONBOARDING'
  } else {
    statusLabel = 'CREATED'
  }

  return {
    isPublished,
    hasTheme: theme_selected,
    requiredCompleteCount: completedRequired.length,
    requiredTotal: requiredFlags.length,
    missingRequiredSteps,
    nextStep,
    statusLabel,
    onboardingFlags: {
      business_details_completed,
      theme_selected,
      catalog_ready,
      payments_configured,
      shipping_configured,
      checkout_configured,
      branding_completed,
      staff_invited,
      policies_added,
      seo_configured,
      analytics_connected
    }
  }
}

/**
 * Creates a new tenant
 * Automatically generates portal URL, admin credentials, and API key
 */
export async function createTenant(tenantData: {
  slug: string
  name: string
  email?: string | null
  phone?: string | null
  country_code?: string | null
  plan: string
  vertical: string
  status?: string
  currency?: string
  enabled_languages?: string[]
  default_language?: string
  catalog_template_id?: string | null
}) {
  const db = getDb()
  
  // Generate tenant credentials
  const adminPassword = generateSecurePassword(16)
  const adminPasswordHash = await hashPassword(adminPassword)
  const adminUsername = `admin_${tenantData.slug}`
  const apiKey = generateApiKey()
  const customerWebsiteUrl = generateCustomerWebsiteUrl(tenantData.slug)
  const adminPortalUrl = generateAdminPortalUrl(tenantData.slug)
  
  const insertData = {
    ...tenantData,
    status: tenantData.status || 'active',
    currency: tenantData.currency || 'INR',
    enabled_languages: tenantData.enabled_languages || ['en'],
    default_language: tenantData.default_language || 'en',
    customer_website_url: customerWebsiteUrl,
    admin_portal_url: adminPortalUrl,
    admin_username: adminUsername,
    admin_password_hash: adminPasswordHash,
    api_key: apiKey,
    updated_at: new Date().toISOString()
  }

  // Log access information being saved (for debugging)
  if (import.meta.env.DEV) {
    console.log('[createTenant] Saving access information:', {
      admin_username: adminUsername,
      admin_portal_url: adminPortalUrl,
      customer_website_url: customerWebsiteUrl,
      api_key: apiKey ? `${apiKey.substring(0, 8)}...` : 'null',
      has_password_hash: !!adminPasswordHash
    })
  }

  const { data, error } = await db
    .from('tenants')
    .insert(insertData)
    .select()
    .single()

  if (error) {
    console.error('[createTenant] Database error:', error)
    throw new Error(`Failed to create tenant: ${error.message}`)
  }

  // Verify access information was saved
  if (import.meta.env.DEV && data) {
    console.log('[createTenant] Tenant created successfully. Access info saved:', {
      admin_username: data.admin_username,
      admin_portal_url: data.admin_portal_url,
      customer_website_url: data.customer_website_url,
      api_key: data.api_key ? `${data.api_key.substring(0, 8)}...` : 'null',
      has_password_hash: !!data.admin_password_hash
    })
  }

  // If catalog_template_id was provided, populate catalog from template
  if (data.catalog_template_id) {
    try {
      await populateCatalogFromTemplate(data.id, data.catalog_template_id)
    } catch (populateError) {
      // Log error but don't fail tenant creation
      console.warn(`[createTenant] Failed to populate catalog from template: ${populateError instanceof Error ? populateError.message : populateError}`)
    }
  }

  // Return data with the plain password for display (this should be shown once and then discarded)
  // In production, you might want to send this via email instead
  return {
    ...data,
    _generated_password: adminPassword // Temporary field, should be removed before storing
  }
}

/**
 * Updates a tenant
 */
export async function updateTenant(tenantId: string, updates: {
  name?: string
  email?: string | null
  phone?: string | null
  country_code?: string | null
  plan?: string
  vertical?: string
  status?: string
  currency?: string
  enabled_languages?: string[]
  default_language?: string
  catalog_template_id?: string | null
}) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update tenant: ${error.message}`)
  }

  return data
}

/**
 * Updates tenant URLs to new format (migration helper)
 * Call this to update existing tenants to use new URL structure
 */
export async function updateTenantUrls(tenantId: string) {
  const db = getDb()
  
  // Fetch tenant to get slug
  const { data: tenant, error: fetchError } = await db
    .from('tenants')
    .select('slug')
    .eq('id', tenantId)
    .single()

  if (fetchError || !tenant) {
    throw new Error(`Failed to fetch tenant: ${fetchError?.message || 'Tenant not found'}`)
  }

  // Generate new URLs
  const customerWebsiteUrl = generateCustomerWebsiteUrl(tenant.slug)
  const adminPortalUrl = generateAdminPortalUrl(tenant.slug)

  // Update URLs
  const { data, error } = await db
    .from('tenants')
    .update({
      customer_website_url: customerWebsiteUrl,
      admin_portal_url: adminPortalUrl,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update tenant URLs: ${error.message}`)
  }

  return data
}

/**
 * Updates all tenant URLs to new format (bulk migration)
 */
export async function updateAllTenantUrls() {
  const db = getDb()
  
  // Fetch all tenants
  const { data: tenants, error: fetchError } = await db
    .from('tenants')
    .select('id, slug')

  if (fetchError) {
    throw new Error(`Failed to fetch tenants: ${fetchError.message}`)
  }

  if (!tenants || tenants.length === 0) {
    return { updated: 0, errors: [] }
  }

  const errors: string[] = []
  let updated = 0

  // Update each tenant
  for (const tenant of tenants) {
    try {
      const customerWebsiteUrl = generateCustomerWebsiteUrl(tenant.slug)
      const adminPortalUrl = generateAdminPortalUrl(tenant.slug)

      const { error: updateError } = await db
        .from('tenants')
        .update({
          customer_website_url: customerWebsiteUrl,
          admin_portal_url: adminPortalUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', tenant.id)

      if (updateError) {
        errors.push(`Tenant ${tenant.slug}: ${updateError.message}`)
      } else {
        updated++
      }
    } catch (error: any) {
      errors.push(`Tenant ${tenant.slug}: ${error.message}`)
    }
  }

  return { updated, errors }
}

/**
 * Deletes a tenant (and all associated data via CASCADE)
 */
export async function deleteTenant(tenantId: string) {
  const db = getDb()
  const { error } = await db
    .from('tenants')
    .delete()
    .eq('id', tenantId)

  if (error) {
    throw new Error(`Failed to delete tenant: ${error.message}`)
  }
}

/**
 * Suspends a tenant
 */
export async function suspendTenant(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      status: 'suspended',
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to suspend tenant: ${error.message}`)
  }

  return data
}

/**
 * Activates a tenant
 */
export async function activateTenant(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      status: 'active',
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to activate tenant: ${error.message}`)
  }

  return data
}

/**
 * Pauses a tenant's subscription
 */
export async function pauseTenantSubscription(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      subscription_status: 'paused',
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to pause subscription: ${error.message}`)
  }

  return data
}

/**
 * Resumes a tenant's subscription
 */
export async function resumeTenantSubscription(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      subscription_status: 'active',
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to resume subscription: ${error.message}`)
  }

  return data
}

/**
 * Publishes a tenant (makes it publicly accessible)
 * Sets status to 'active' and published_at timestamp
 */
export async function publishTenant(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      status: 'active',
      published_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to publish tenant: ${error.message}`)
  }

  return data
}

/**
 * Unpublishes a tenant (makes it private)
 * Keeps status as 'active' but removes published_at
 */
export async function unpublishTenant(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      published_at: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to unpublish tenant: ${error.message}`)
  }

  return data
}

/**
 * Gets catalog counts (products and categories) for a tenant
 * Categories are counted from unique category values in catalog items
 */
export async function getCatalogCounts(tenantId: string): Promise<{ products: number; categories: number }> {
  const db = getDb()
  
  try {
    // Count products
    const { count: productsCount, error: productsError } = await db
      .from('catalog')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)

    if (productsError) {
      console.warn('[getCatalogCounts] Failed to count products:', productsError.message)
    }

    // Count unique categories from catalog items
    // Categories are stored in the catalog table's category field, not a separate table
    let categoriesCount = 0
    try {
      const { data: catalogItems, error: categoriesError } = await db
        .from('catalog')
        .select('category')
        .eq('tenant_id', tenantId)
        .not('category', 'is', null)

      if (categoriesError) {
        console.warn('[getCatalogCounts] Failed to fetch categories:', categoriesError.message)
      } else if (catalogItems && catalogItems.length > 0) {
        // Count unique categories
        const uniqueCategories = new Set(
          catalogItems
            .map(item => item.category)
            .filter(cat => cat && cat.trim() !== '')
        )
        categoriesCount = uniqueCategories.size
      }
    } catch (error: any) {
      // Silently handle any errors - categories count will remain 0
      if (import.meta.env.DEV) {
        console.warn('[getCatalogCounts] Error counting categories:', error.message)
      }
    }

    return {
      products: productsCount || 0,
      categories: categoriesCount
    }
  } catch (error) {
    console.error('[getCatalogCounts] Error:', error)
    return { products: 0, categories: 0 }
  }
}

/**
 * Checks if tenant has staff users (more than just the owner)
 */
export async function checkStaffCount(tenantId: string): Promise<number> {
  const db = getDb()
  
  try {
    const { count, error } = await db
      .from('tenant_users')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)

    if (error) {
      console.warn('[checkStaffCount] Failed to count staff:', error.message)
      return 0
    }

    return count || 0
  } catch {
    // tenant_users table might not exist
    return 0
  }
}

/**
 * Checks if tenant has policies configured
 */
export async function checkPolicies(tenantId: string): Promise<boolean> {
  const db = getDb()
  
  try {
    // Check if policies exist in settings_values table
    const { data, error } = await db
      .from('settings_values')
      .select('id')
      .eq('tenant_id', tenantId)
      .in('setting_key', ['return_policy', 'privacy_policy', 'terms_of_service', 'refund_policy'])
      .limit(1)

    if (error) {
      console.warn('[checkPolicies] Failed to check policies:', error.message)
      return false
    }

    return (data && data.length > 0) || false
  } catch {
    return false
  }
}

/**
 * Checks if tenant has checkout configured
 * Requires: policies (T&C, Privacy, Returns) and checkout fields configured
 */
export async function hasCheckoutConfigured(tenantId: string): Promise<boolean> {
  const db = getDb()
  
  try {
    // Check if policies exist
    const policiesConfigured = await checkPolicies(tenantId)
    
    // Check if checkout fields are configured (checkout_fields setting)
    const { data: checkoutFields, error: checkoutError } = await db
      .from('settings_values')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('setting_key', 'checkout_fields')
      .limit(1)

    if (checkoutError) {
      console.warn('[hasCheckoutConfigured] Failed to check checkout fields:', checkoutError.message)
    }

    // Checkout is configured if policies exist OR checkout_fields setting exists
    // In production, you might want both, but for now we'll be lenient
    return policiesConfigured || (checkoutFields && checkoutFields.length > 0)
  } catch {
    return false
  }
}

/**
 * ============================================================================
 * GRIEVANCE REDRESSAL FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches grievances for a tenant
 */
export async function fetchGrievances(tenantId: string, filters?: {
  status?: string
  priority?: string
  category?: string
  assigned_to?: string
  customer_id?: string
  order_id?: string
}) {
  const db = getDb()
  let query = db
    .from('grievances')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }
  if (filters?.priority) {
    query = query.eq('priority', filters.priority)
  }
  if (filters?.category) {
    query = query.eq('category', filters.category)
  }
  if (filters?.assigned_to) {
    query = query.eq('assigned_to_user_id', filters.assigned_to)
  }
  if (filters?.customer_id) {
    query = query.eq('customer_id', filters.customer_id)
  }
  if (filters?.order_id) {
    query = query.eq('order_id', filters.order_id)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Failed to fetch grievances: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches a single grievance by ID
 */
export async function fetchGrievanceById(grievanceId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('grievances')
    .select('*')
    .eq('id', grievanceId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch grievance: ${error.message}`)
  }

  return data
}

/**
 * Creates a new grievance
 */
export async function createGrievance(grievanceData: {
  tenant_id: string
  customer_id?: string
  order_id?: string
  created_by_user_id?: string
  subject: string
  description: string
  category?: 'Delivery' | 'Payment' | 'Product' | 'Service' | 'Other'
  priority?: 'LOW' | 'MED' | 'HIGH'
  attachments?: any[]
}) {
  const db = getDb()
  
  // Get tenant slug for ticket number generation
  const { data: tenant } = await db
    .from('tenants')
    .select('slug')
    .eq('id', grievanceData.tenant_id)
    .single()

  if (!tenant) {
    throw new Error('Tenant not found')
  }

  // Generate ticket number (simple implementation)
  const ticketPrefix = tenant.slug.substring(0, 3).toUpperCase() + '-'
  const { count } = await db
    .from('grievances')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', grievanceData.tenant_id)
    .like('ticket_no', ticketPrefix + '%')
  
  const ticketSeq = (count || 0) + 1
  const ticket_no = ticketPrefix + String(ticketSeq).padStart(6, '0')

  // Calculate SLA deadlines (24 hours for first response, 72 hours for resolution for HIGH priority)
  const now = new Date()
  const firstResponseDue = new Date(now.getTime() + 24 * 60 * 60 * 1000)
  const resolutionDue = new Date(now.getTime() + (grievanceData.priority === 'HIGH' ? 48 : 72) * 60 * 60 * 1000)

  // Auto-assign to support manager or owner (simplified - in production, use role-based assignment)
  const { data: supportManager } = await db
    .from('tenant_users')
    .select('id')
    .eq('tenant_id', grievanceData.tenant_id)
    .eq('role', 'manager')
    .eq('status', 'active')
    .limit(1)
    .single()
    .catch(() => ({ data: null }))

  const { data, error } = await db
    .from('grievances')
    .insert({
      tenant_id: grievanceData.tenant_id,
      ticket_no,
      customer_id: grievanceData.customer_id || null,
      order_id: grievanceData.order_id || null,
      created_by_user_id: grievanceData.created_by_user_id || null,
      assigned_to_user_id: supportManager?.id || null,
      subject: grievanceData.subject,
      description: grievanceData.description,
      category: grievanceData.category || 'Other',
      priority: grievanceData.priority || 'MED',
      first_response_due_at: firstResponseDue.toISOString(),
      resolution_due_at: resolutionDue.toISOString(),
      attachments: grievanceData.attachments || []
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create grievance: ${error.message}`)
  }

  return data
}

/**
 * Updates a grievance
 */
export async function updateGrievance(
  grievanceId: string,
  updates: {
    status?: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'
    priority?: 'LOW' | 'MED' | 'HIGH'
    category?: string
    assigned_to_user_id?: string
    resolution_summary?: string
  }
) {
  const db = getDb()
  const updateData: any = {
    ...updates,
    updated_at: new Date().toISOString()
  }

  // Set timestamps based on status changes
  if (updates.status === 'IN_PROGRESS' && !updates.resolution_summary) {
    // Don't overwrite if already set
    const { data: existing } = await db
      .from('grievances')
      .select('first_response_at')
      .eq('id', grievanceId)
      .single()
    
    if (!existing?.first_response_at) {
      updateData.first_response_at = new Date().toISOString()
    }
  }
  
  if (updates.status === 'RESOLVED') {
    updateData.resolved_at = new Date().toISOString()
  }
  
  if (updates.status === 'CLOSED') {
    updateData.closed_at = new Date().toISOString()
    if (!updateData.resolved_at) {
      updateData.resolved_at = new Date().toISOString()
    }
  }

  const { data, error } = await db
    .from('grievances')
    .update(updateData)
    .eq('id', grievanceId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update grievance: ${error.message}`)
  }

  return data
}

/**
 * Fetches comments for a grievance
 */
export async function fetchGrievanceComments(grievanceId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('grievance_comments')
    .select('*')
    .eq('grievance_id', grievanceId)
    .order('created_at', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch comments: ${error.message}`)
  }

  return data || []
}

/**
 * Adds a comment to a grievance
 */
export async function addGrievanceComment(commentData: {
  grievance_id: string
  user_id?: string
  comment_type?: 'comment' | 'status_change' | 'assignment' | 'internal_note' | 'customer_reply'
  content: string
  is_internal?: boolean
  attachments?: any[]
}) {
  const db = getDb()
  const { data, error } = await db
    .from('grievance_comments')
    .insert({
      grievance_id: commentData.grievance_id,
      user_id: commentData.user_id || null,
      comment_type: commentData.comment_type || 'comment',
      content: commentData.content,
      is_internal: commentData.is_internal || false,
      attachments: commentData.attachments || []
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to add comment: ${error.message}`)
  }

  return data
}

/**
 * Gets grievance statistics for a tenant
 */
export async function getGrievanceStats(tenantId: string) {
  const db = getDb()
  
  try {
    const { data: grievances } = await db
      .from('grievances')
      .select('status, priority, category, first_response_at, resolved_at, first_response_due_at, resolution_due_at')
      .eq('tenant_id', tenantId)

    if (!grievances || grievances.length === 0) {
      return {
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        avgResponseTime: 0,
        slaBreaches: 0,
        byCategory: {},
        byPriority: {}
      }
    }

    const stats = {
      total: grievances.length,
      open: grievances.filter((g: any) => g.status === 'OPEN').length,
      inProgress: grievances.filter((g: any) => g.status === 'IN_PROGRESS').length,
      resolved: grievances.filter((g: any) => g.status === 'RESOLVED').length,
      closed: grievances.filter((g: any) => g.status === 'CLOSED').length,
      avgResponseTime: 0,
      slaBreaches: 0,
      byCategory: {} as Record<string, number>,
      byPriority: {} as Record<string, number>
    }

    // Calculate average response time
    const respondedGrievances = grievances.filter((g: any) => g.first_response_at)
    if (respondedGrievances.length > 0) {
      const totalResponseTime = respondedGrievances.reduce((sum: number, g: any) => {
        const responseTime = new Date(g.first_response_at).getTime() - new Date(g.created_at || g.first_response_due_at).getTime()
        return sum + responseTime
      }, 0)
      stats.avgResponseTime = Math.round(totalResponseTime / respondedGrievances.length / (60 * 60 * 1000)) // hours
    }

    // Count SLA breaches
    stats.slaBreaches = grievances.filter((g: any) => {
      if (g.first_response_due_at && !g.first_response_at) {
        return new Date() > new Date(g.first_response_due_at)
      }
      if (g.resolution_due_at && g.status !== 'RESOLVED' && g.status !== 'CLOSED') {
        return new Date() > new Date(g.resolution_due_at)
      }
      return false
    }).length

    // Count by category
    grievances.forEach((g: any) => {
      stats.byCategory[g.category] = (stats.byCategory[g.category] || 0) + 1
      stats.byPriority[g.priority] = (stats.byPriority[g.priority] || 0) + 1
    })

    return stats
  } catch (error) {
    console.error('[getGrievanceStats] Error:', error)
    return {
      total: 0,
      open: 0,
      inProgress: 0,
      resolved: 0,
      closed: 0,
      avgResponseTime: 0,
      slaBreaches: 0,
      byCategory: {},
      byPriority: {}
    }
  }
}

/**
 * ============================================================================
 * TENANT USER MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches all users for a tenant
 */
export async function fetchTenantUsers(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_users')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch tenant users: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches a single tenant user by ID
 */
export async function fetchTenantUserById(userId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_users')
    .select('*')
    .eq('id', userId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch tenant user: ${error.message}`)
  }

  return data
}

/**
 * Creates a new tenant user
 */
export async function createTenantUser(userData: {
  tenant_id: string
  email: string
  name?: string
  role?: 'owner' | 'manager' | 'staff'
  status?: 'active' | 'inactive' | 'suspended'
  phone?: string
}) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_users')
    .insert({
      tenant_id: userData.tenant_id,
      email: userData.email,
      name: userData.name || null,
      role: userData.role || 'staff',
      status: userData.status || 'active',
      phone: userData.phone || null
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create tenant user: ${error.message}`)
  }

  return data
}

/**
 * Updates a tenant user
 */
export async function updateTenantUser(
  userId: string,
  updates: {
    email?: string
    name?: string
    role?: 'owner' | 'manager' | 'staff'
    status?: 'active' | 'inactive' | 'suspended'
    phone?: string
  }
) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_users')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update tenant user: ${error.message}`)
  }

  return data
}

/**
 * Deletes a tenant user
 */
export async function deleteTenantUser(userId: string) {
  const db = getDb()
  const { error } = await db
    .from('tenant_users')
    .delete()
    .eq('id', userId)

  if (error) {
    throw new Error(`Failed to delete tenant user: ${error.message}`)
  }

  return true
}

/**
 * Updates last login timestamp for a tenant user
 */
export async function updateTenantUserLastLogin(userId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_users')
    .update({
      last_login_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()

  if (error) {
    console.warn(`[updateTenantUserLastLogin] Failed to update last login: ${error.message}`)
    return null
  }

  return data
}

/**
 * ============================================================================
 * USER ACTIVITY & DASHBOARD FUNCTIONS
 * ============================================================================
 */

/**
 * Gets recent user activities for a tenant
 */
export async function getTenantUserActivities(tenantId: string, limit: number = 20) {
  const db = getDb()
  
  try {
    // Get recent analytics events
    const { data: events, error: eventsError } = await db
      .from('analytics_events')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (eventsError) {
      console.warn('[getTenantUserActivities] Failed to fetch analytics events:', eventsError.message)
    }

    // Get recent orders
    const { data: orders, error: ordersError } = await db
      .from('orders')
      .select('id, customer_id, total, status, created_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (ordersError) {
      console.warn('[getTenantUserActivities] Failed to fetch orders:', ordersError.message)
    }

    // Combine and sort by date
    const activities: any[] = []
    
    if (events) {
      events.forEach(event => {
        activities.push({
          type: 'event',
          id: event.id,
          description: `${event.event_category}: ${event.event_name}`,
          timestamp: event.created_at,
          user_id: event.user_id,
          properties: event.properties
        })
      })
    }

    if (orders) {
      orders.forEach(order => {
        activities.push({
          type: 'order',
          id: order.id,
          description: `New order: ₹${parseFloat(order.total || '0').toLocaleString('en-IN')}`,
          timestamp: order.created_at,
          status: order.status
        })
      })
    }

    // Sort by timestamp descending
    activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

    return activities.slice(0, limit)
  } catch (error) {
    console.warn('[getTenantUserActivities] Error fetching activities:', error)
    return []
  }
}

/**
 * Gets dashboard statistics for a tenant based on real data
 */
export async function getTenantDashboardStats(tenantId: string) {
  const db = getDb()
  
  try {
    // Get orders stats
    const { data: orders, error: ordersError } = await db
      .from('orders')
      .select('id, total, status, created_at')
      .eq('tenant_id', tenantId)

    if (ordersError) {
      console.warn('[getTenantDashboardStats] Failed to fetch orders:', ordersError.message)
    }

    // Get customers count
    const { count: customersCount, error: customersError } = await db
      .from('customers')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)

    if (customersError) {
      console.warn('[getTenantDashboardStats] Failed to fetch customers:', customersError.message)
    }

    // Get catalog counts
    const catalogCounts = await getCatalogCounts(tenantId)

    // Calculate revenue
    const revenue = orders?.reduce((sum: number, order: any) => {
      return sum + (parseFloat(order.total || '0') || 0)
    }, 0) || 0

    // Get recent orders (last 5)
    const recentOrders = orders
      ?.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5)
      .map((order: any) => ({
        id: order.id,
        total: parseFloat(order.total || '0') || 0,
        status: order.status || 'pending',
        createdAt: order.created_at
      })) || []

    // Get active users count
    const { count: activeUsersCount, error: usersError } = await db
      .from('tenant_users')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('status', 'active')

    if (usersError) {
      console.warn('[getTenantDashboardStats] Failed to fetch active users:', usersError.message)
    }

    return {
      totalOrders: orders?.length || 0,
      activeCustomers: customersCount || 0,
      revenue,
      avgOrderValue: orders && orders.length > 0 ? revenue / orders.length : 0,
      totalProducts: catalogCounts.products,
      totalCategories: catalogCounts.categories,
      activeUsers: activeUsersCount || 0,
      recentOrders
    }
  } catch (error) {
    console.error('[getTenantDashboardStats] Error:', error)
    return {
      totalOrders: 0,
      activeCustomers: 0,
      revenue: 0,
      avgOrderValue: 0,
      totalProducts: 0,
      totalCategories: 0,
      activeUsers: 0,
      recentOrders: []
    }
  }
}

/**
 * Resets tenant admin password
 * Generates a new password and returns it (should be shown only once)
 */
export async function resetTenantPassword(tenantId: string): Promise<{ username: string; password: string }> {
  const db = getDb()
  
  // Generate a secure random password using the same function as createTenant
  const newPassword = generateSecurePassword(16)
  const adminPasswordHash = await hashPassword(newPassword)

  // Update tenant password hash
  const { data: tenant, error } = await db
    .from('tenants')
    .update({
      admin_password_hash: adminPasswordHash,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select('admin_username, slug')
    .single()

  if (error) {
    throw new Error(`Failed to reset password: ${error.message}`)
  }

  return {
    username: tenant.admin_username || `admin_${tenant.slug}`,
    password: newPassword
  }
}

/**
 * Platform Management Functions
 */


/**
 * Fetches all countries
 */
export async function fetchCountries() {
  const db = getDb()
  const { data, error } = await db
    .from('countries')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch countries: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches all languages
 */
export async function fetchLanguages() {
  const db = getDb()
  const { data, error } = await db
    .from('languages')
    .select('*')
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch languages: ${error.message}`)
  }

  return data || []
}

/**
 * Catalog Template Management Functions
 */

/**
 * Fetches all catalog templates (for platform admin)
 * @param status - Optional filter by status ('active', 'inactive', 'draft')
 * @param businessCategory - Optional filter by business category
 * @param planSlug - Optional filter by applicable plan
 */
export async function fetchCatalogTemplates(options?: {
  status?: 'active' | 'inactive' | 'draft'
  businessCategory?: string
  planSlug?: string
}) {
  const db = getDb()
  let query = db
    .from('catalog_templates')
    .select('*')

  if (options?.status) {
    query = query.eq('status', options.status)
  }

  if (options?.businessCategory) {
    query = query.eq('business_category', options.businessCategory)
  }

  if (options?.planSlug) {
    query = query.contains('applicable_plans', [options.planSlug])
  }

  const { data, error } = await query
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch catalog templates: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches a single catalog template by ID
 */
export async function fetchCatalogTemplateById(templateId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog_templates')
    .select('*')
    .eq('id', templateId)
    .single()

  if (error) {
    throw new Error(`Failed to fetch catalog template: ${error.message}`)
  }

  return data
}

/**
 * Creates a new catalog template (platform admin only)
 */
export async function createCatalogTemplate(templateData: {
  name: string
  description?: string
  business_category?: string
  business_segments?: string[]
  applicable_plans?: string[]
  enabled_modules?: string[]
  status?: 'active' | 'inactive' | 'draft'
  display_order?: number
}) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog_templates')
    .insert({
      name: templateData.name,
      description: templateData.description || null,
      business_category: templateData.business_category || null,
      business_segments: templateData.business_segments || [],
      applicable_plans: templateData.applicable_plans || [],
      enabled_modules: templateData.enabled_modules || [],
      status: templateData.status || 'draft',
      display_order: templateData.display_order || 0
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create catalog template: ${error.message}`)
  }

  return data
}

/**
 * Updates an existing catalog template (platform admin only)
 */
export async function updateCatalogTemplate(
  templateId: string,
  updates: {
    name?: string
    description?: string
    business_category?: string
    business_segments?: string[]
    applicable_plans?: string[]
    enabled_modules?: string[]
    status?: 'active' | 'inactive' | 'draft'
    display_order?: number
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  if (updates.name !== undefined) updateData.name = updates.name
  if (updates.description !== undefined) updateData.description = updates.description
  if (updates.business_category !== undefined) updateData.business_category = updates.business_category
  if (updates.business_segments !== undefined) updateData.business_segments = updates.business_segments
  if (updates.applicable_plans !== undefined) updateData.applicable_plans = updates.applicable_plans
  if (updates.enabled_modules !== undefined) updateData.enabled_modules = updates.enabled_modules
  if (updates.status !== undefined) updateData.status = updates.status
  if (updates.display_order !== undefined) updateData.display_order = updates.display_order

  const { data, error } = await db
    .from('catalog_templates')
    .update(updateData)
    .eq('id', templateId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update catalog template: ${error.message}`)
  }

  return data
}

/**
 * Deletes a catalog template (platform admin only)
 * Note: This will set catalog_template_id to NULL for tenants using this template
 */
export async function deleteCatalogTemplate(templateId: string) {
  const db = getDb()
  const { error } = await db
    .from('catalog_templates')
    .delete()
    .eq('id', templateId)

  if (error) {
    throw new Error(`Failed to delete catalog template: ${error.message}`)
  }

  return { success: true }
}

/**
 * Fetches available catalog templates for a tenant based on their plan
 */
export async function fetchAvailableCatalogTemplatesForTenant(tenantPlan: string) {
  const db = getDb()
  const { data, error } = await db
    .from('catalog_templates')
    .select('*')
    .eq('status', 'active')
    .contains('applicable_plans', [tenantPlan])
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch available catalog templates: ${error.message}`)
  }

  return data || []
}

/**
 * Gets the catalog template assigned to a tenant
 */
export async function getTenantCatalogTemplate(tenantSlug: string) {
  const db = getDb()
  
  // First get the tenant
  const { data: tenant, error: tenantError } = await db
    .from('tenants')
    .select('id, catalog_template_id, plan')
    .eq('slug', tenantSlug)
    .single()

  if (tenantError) {
    throw new Error(`Failed to fetch tenant: ${tenantError.message}`)
  }

  if (!tenant.catalog_template_id) {
    return null
  }

  // Get the template
  const { data: template, error: templateError } = await db
    .from('catalog_templates')
    .select('*')
    .eq('id', tenant.catalog_template_id)
    .single()

  if (templateError) {
    throw new Error(`Failed to fetch catalog template: ${templateError.message}`)
  }

  return template
}

/**
 * Populates tenant catalog from template sample items
 * Only populates if tenant catalog is empty (prevents overwriting existing items)
 * Uses SECURITY DEFINER database function to bypass RLS and work with any auth method
 */
export async function populateCatalogFromTemplate(tenantId: string, templateId: string) {
  const db = getDb()

  // Use SECURITY DEFINER database function to populate catalog
  // This bypasses RLS and works regardless of authentication method
  const { data, error } = await db.rpc('populate_catalog_from_template', {
    tenant_id_param: tenantId,
    template_id_param: templateId
  })

  if (error) {
    throw new Error(`Failed to populate catalog from template: ${error.message}`)
  }

  // The function returns JSONB with populated status and count
  if (data && typeof data === 'object') {
    return {
      populated: data.populated === true,
      count: data.count || 0,
      reason: data.reason || undefined
    }
  }

  // Fallback if function returns unexpected format
  return { populated: false, reason: 'Unexpected response from database function' }
}

/**
 * Assigns a catalog template to a tenant
 * Validates that the template is applicable to the tenant's plan
 * Automatically populates catalog from template if tenant catalog is empty
 */
export async function assignCatalogTemplateToTenant(
  tenantId: string,
  templateId: string,
  options?: { populateCatalog?: boolean }
) {
  const db = getDb()

  // First, get the tenant to check their plan
  const { data: tenant, error: tenantError } = await db
    .from('tenants')
    .select('plan')
    .eq('id', tenantId)
    .single()

  if (tenantError) {
    throw new Error(`Failed to fetch tenant: ${tenantError.message}`)
  }

  // Get the full template to validate and extract properties
  const { data: template, error: templateError } = await db
    .from('catalog_templates')
    .select('applicable_plans, status, enabled_modules, business_segments, business_category, name')
    .eq('id', templateId)
    .single()

  if (templateError) {
    throw new Error(`Failed to fetch catalog template: ${templateError.message}`)
  }

  if (template.status !== 'active') {
    throw new Error('Cannot assign inactive or draft template to tenant')
  }

  if (!template.applicable_plans.includes(tenant.plan)) {
    throw new Error(
      `Template is not applicable to tenant's plan (${tenant.plan}). ` +
      `Template supports: ${template.applicable_plans.join(', ')}`
    )
  }

  // Assign the template to tenant
  const { data, error } = await db
    .from('tenants')
    .update({
      catalog_template_id: templateId,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to assign catalog template: ${error.message}`)
  }

  // Save template properties to store configuration
  // Store the template's enabled modules, business segments, and category in tenant settings
  try {
    // Check if setting schema exists
    const { data: schema } = await db
      .from('settings_schema')
      .select('*')
      .eq('key', 'store_config')
      .single()

    if (schema) {
      // Upsert setting value directly
      await db
        .from('settings_values')
        .upsert({
          setting_key: 'store_config',
          tenant_id: tenantId,
          value: {
            enabled_modules: template.enabled_modules || [],
            business_segments: template.business_segments || [],
            business_category: template.business_category || null,
            catalog_template_id: templateId,
            catalog_template_name: template.name || null
          },
          updated_by: null
        }, {
          onConflict: 'setting_key,tenant_id'
        })
    } else {
      // If schema doesn't exist, log a warning but don't fail
      console.warn('store_config setting schema not found. Please run migration 032_add_store_config_setting.sql')
    }
  } catch (configError) {
    // Log error but don't fail the assignment
    console.warn(`Failed to save template properties to store configuration: ${configError instanceof Error ? configError.message : configError}`)
  }

  // Populate catalog from template if requested and tenant catalog is empty
  if (options?.populateCatalog !== false) {
    try {
      await populateCatalogFromTemplate(tenantId, templateId)
    } catch (populateError) {
      // Log error but don't fail the assignment
      console.warn(`Failed to populate catalog from template: ${populateError instanceof Error ? populateError.message : populateError}`)
    }
  }

  return data
}

/**
 * Removes catalog template assignment from a tenant
 */
export async function unassignCatalogTemplateFromTenant(tenantId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenants')
    .update({
      catalog_template_id: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', tenantId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to unassign catalog template: ${error.message}`)
  }

  return data
}

/**
 * Checks if a tenant slug is available
 */
export async function checkTenantSlugAvailability(slug: string, excludeTenantId?: string): Promise<boolean> {
  const db = getDb()
  let query = db
    .from('tenants')
    .select('id')
    .eq('slug', slug)
    .limit(1)

  if (excludeTenantId) {
    query = query.neq('id', excludeTenantId)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Failed to check slug availability: ${error.message}`)
  }

  return !data || data.length === 0
}

/**
 * ============================================================================
 * PLANS MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches all plans (with optional filters)
 * Defaults to active plans for backward compatibility
 */
export async function fetchPlans(options?: { isActive?: boolean }) {
  const db = getDb()
  let query = db
    .from('plans')
    .select('*')

  // Default to active plans if not specified (backward compatibility)
  if (options?.isActive !== undefined) {
    query = query.eq('is_active', options.isActive)
  } else {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query
    .order('display_order', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch plans: ${error.message}`)
  }

  return data || []
}

/**
 * Fetches a single plan by ID or slug
 */
export async function fetchPlanById(planId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('plans')
    .select('*')
    .or(`id.eq.${planId},slug.eq.${planId}`)
    .single()

  if (error) {
    throw new Error(`Failed to fetch plan: ${error.message}`)
  }

  return data
}

/**
 * Creates a new plan (platform admin only)
 */
export async function createPlan(planData: {
  slug: string
  name: string
  description?: string
  price_monthly: number
  price_yearly?: number
  features?: any[]
  max_tenants?: number
  max_users?: number
  max_storage_gb?: number
  is_active?: boolean
  display_order?: number
}) {
  const db = getDb()
  const { data, error } = await db
    .from('plans')
    .insert({
      slug: planData.slug,
      name: planData.name,
      description: planData.description || null,
      price_monthly: planData.price_monthly,
      price_yearly: planData.price_yearly || null,
      features: planData.features || [],
      max_tenants: planData.max_tenants || null,
      max_users: planData.max_users || null,
      max_storage_gb: planData.max_storage_gb || null,
      is_active: planData.is_active !== undefined ? planData.is_active : true,
      display_order: planData.display_order || 0
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create plan: ${error.message}`)
  }

  return data
}

/**
 * Updates an existing plan (platform admin only)
 */
export async function updatePlan(
  planId: string,
  updates: {
    slug?: string
    name?: string
    description?: string
    price_monthly?: number
    price_yearly?: number
    features?: any[]
    max_tenants?: number
    max_users?: number
    max_storage_gb?: number
    is_active?: boolean
    display_order?: number
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  Object.keys(updates).forEach(key => {
    if (updates[key as keyof typeof updates] !== undefined) {
      updateData[key] = updates[key as keyof typeof updates]
    }
  })

  const { data, error } = await db
    .from('plans')
    .update(updateData)
    .or(`id.eq.${planId},slug.eq.${planId}`)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update plan: ${error.message}`)
  }

  return data
}

/**
 * Deletes a plan (platform admin only)
 */
export async function deletePlan(planId: string) {
  const db = getDb()
  const { error } = await db
    .from('plans')
    .delete()
    .or(`id.eq.${planId},slug.eq.${planId}`)

  if (error) {
    throw new Error(`Failed to delete plan: ${error.message}`)
  }

  return { success: true }
}

/**
 * ============================================================================
 * BILLING & INVOICES MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches invoices with optional filters
 */
export async function fetchInvoices(options?: {
  tenantId?: string
  status?: string
  startDate?: string
  endDate?: string
  limit?: number
}) {
  const db = getDb()
  let query = db
    .from('invoices')
    .select(`
      *,
      tenants:tenant_id (id, slug, name),
      plans:plan_id (id, slug, name)
    `)

  if (options?.tenantId) {
    query = query.eq('tenant_id', options.tenantId)
  }

  if (options?.status) {
    query = query.eq('status', options.status)
  }

  if (options?.startDate) {
    query = query.gte('invoice_date', options.startDate)
  }

  if (options?.endDate) {
    query = query.lte('invoice_date', options.endDate)
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(options?.limit || 100)

  if (error) {
    throw new Error(`Failed to fetch invoices: ${error.message}`)
  }

  return data || []
}

/**
 * Creates a new invoice
 */
export async function createInvoice(invoiceData: {
  tenant_id: string
  plan_id?: string
  amount: number
  tax_amount?: number
  currency?: string
  due_date: string
  billing_period_start?: string
  billing_period_end?: string
  notes?: string
  metadata?: any
}) {
  const db = getDb()

  // Generate invoice number
  const { data: invoiceNumber } = await db.rpc('generate_invoice_number')

  const totalAmount = invoiceData.amount + (invoiceData.tax_amount || 0)

  const { data, error } = await db
    .from('invoices')
    .insert({
      tenant_id: invoiceData.tenant_id,
      plan_id: invoiceData.plan_id || null,
      invoice_number: invoiceNumber || `INV-${Date.now()}`,
      amount: invoiceData.amount,
      tax_amount: invoiceData.tax_amount || 0,
      total_amount: totalAmount,
      currency: invoiceData.currency || 'INR',
      due_date: invoiceData.due_date,
      billing_period_start: invoiceData.billing_period_start || null,
      billing_period_end: invoiceData.billing_period_end || null,
      notes: invoiceData.notes || null,
      metadata: invoiceData.metadata || {}
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create invoice: ${error.message}`)
  }

  return data
}

/**
 * Updates an invoice
 */
export async function updateInvoice(
  invoiceId: string,
  updates: {
    status?: string
    paid_date?: string
    payment_method?: string
    payment_reference?: string
    notes?: string
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  Object.keys(updates).forEach(key => {
    if (updates[key as keyof typeof updates] !== undefined) {
      updateData[key] = updates[key as keyof typeof updates]
    }
  })

  const { data, error } = await db
    .from('invoices')
    .update(updateData)
    .eq('id', invoiceId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update invoice: ${error.message}`)
  }

  return data
}

/**
 * Gets billing statistics
 */
export async function getBillingStatistics(options?: {
  tenantId?: string
  startDate?: string
  endDate?: string
}) {
  const db = getDb()

  const startDate = options?.startDate || new Date(new Date().setDate(1)).toISOString()
  const endDate = options?.endDate || new Date().toISOString()

  const { data, error } = await db.rpc('get_revenue_metrics', {
    start_date: startDate,
    end_date: endDate,
    tenant_id_param: options?.tenantId || null
  })

  if (error) {
    throw new Error(`Failed to fetch billing statistics: ${error.message}`)
  }

  return data?.[0] || null
}

/**
 * ============================================================================
 * ANALYTICS & REPORTS FUNCTIONS
 * ============================================================================
 */

/**
 * Records an analytics event
 */
export async function recordAnalyticsEvent(eventData: {
  tenant_id?: string
  event_type: string
  event_category: string
  event_name: string
  properties?: any
  user_id?: string
  session_id?: string
}) {
  const db = getDb()
  const { data, error } = await db
    .from('analytics_events')
    .insert({
      tenant_id: eventData.tenant_id || null,
      event_type: eventData.event_type,
      event_category: eventData.event_category,
      event_name: eventData.event_name,
      properties: eventData.properties || {},
      user_id: eventData.user_id || null,
      session_id: eventData.session_id || null
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to record analytics event: ${error.message}`)
  }

  return data
}

/**
 * Gets analytics metrics
 */
export async function getAnalyticsMetrics(options?: {
  tenantId?: string
  metricType?: string
  metricName?: string
  periodType?: string
  startDate?: string
  endDate?: string
}) {
  const db = getDb()
  let query = db
    .from('analytics_metrics')
    .select('*')

  if (options?.tenantId) {
    query = query.eq('tenant_id', options.tenantId)
  }

  if (options?.metricType) {
    query = query.eq('metric_type', options.metricType)
  }

  if (options?.metricName) {
    query = query.eq('metric_name', options.metricName)
  }

  if (options?.periodType) {
    query = query.eq('period_type', options.periodType)
  }

  if (options?.startDate) {
    query = query.gte('period_start', options.startDate)
  }

  if (options?.endDate) {
    query = query.lte('period_end', options.endDate)
  }

  const { data, error } = await query
    .order('period_start', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch analytics metrics: ${error.message}`)
  }

  return data || []
}

/**
 * Gets revenue metrics
 */
export async function getRevenueMetrics(options?: {
  tenantId?: string
  startDate?: string
  endDate?: string
}) {
  const db = getDb()

  const startDate = options?.startDate || new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString()
  const endDate = options?.endDate || new Date().toISOString()

  const { data, error } = await db.rpc('get_revenue_metrics', {
    start_date: startDate,
    end_date: endDate,
    tenant_id_param: options?.tenantId || null
  })

  if (error) {
    throw new Error(`Failed to fetch revenue metrics: ${error.message}`)
  }

  return data?.[0] || null
}

/**
 * Gets tenant growth metrics
 */
export async function getTenantGrowthMetrics(options?: {
  startDate?: string
  endDate?: string
}) {
  const db = getDb()

  const startDate = options?.startDate || new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString()
  const endDate = options?.endDate || new Date().toISOString()

  const { data, error } = await db.rpc('get_tenant_growth_metrics', {
    start_date: startDate,
    end_date: endDate
  })

  if (error) {
    throw new Error(`Failed to fetch tenant growth metrics: ${error.message}`)
  }

  return data?.[0] || null
}

/**
 * Gets platform dashboard metrics
 * Aggregates data from tenants, subscriptions, invoices, and users
 */
export async function getPlatformDashboardMetrics() {
  const db = getDb()
  
  try {
    // Fetch all tenants
    const { data: tenants, error: tenantsError } = await db
      .from('tenants')
      .select('id, status, plan, created_at')
    
    if (tenantsError) throw tenantsError

    // Fetch all subscriptions
    const { data: subscriptions, error: subscriptionsError } = await db
      .from('subscriptions')
      .select('id, tenant_id, status, trial_end, current_period_end')
    
    if (subscriptionsError) throw subscriptionsError

    // Fetch invoices for revenue calculation
    const { data: invoices, error: invoicesError } = await db
      .from('invoices')
      .select('id, tenant_id, total_amount, status, invoice_date, created_at')
      .order('created_at', { ascending: false })
    
    if (invoicesError) throw invoicesError

    // Fetch tenant users for active users count
    const { data: tenantUsers, error: usersError } = await db
      .from('tenant_users')
      .select('id, tenant_id, status')
    
    if (usersError) {
      // tenant_users table might not exist, that's okay
      console.warn('[getPlatformDashboardMetrics] tenant_users table not found, skipping active users count')
    }

    // Calculate metrics
    const now = new Date()
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const todayStart = new Date(now.setHours(0, 0, 0, 0))
    const lastMonthStart = new Date(oneMonthAgo.setHours(0, 0, 0, 0))

    // Total and active tenants
    const totalTenants = tenants?.length || 0
    const activeTenants = tenants?.filter(t => t.status === 'active').length || 0

    // New signups
    const newSignupsToday = tenants?.filter(t => 
      new Date(t.created_at) >= todayStart
    ).length || 0
    
    const newSignupsThisWeek = tenants?.filter(t => 
      new Date(t.created_at) >= oneWeekAgo
    ).length || 0

    // Subscriptions
    const activeSubscriptions = subscriptions?.filter(s => s.status === 'active').length || 0
    const trialSubscriptions = subscriptions?.filter(s => 
      s.status === 'trial' || (s.trial_end && new Date(s.trial_end) > now)
    ).length || 0

    // Revenue calculations
    const paidInvoices = invoices?.filter(i => i.status === 'paid') || []
    const totalRevenue = paidInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount?.toString() || '0'), 0)
    
    // Monthly revenue (last 30 days)
    const lastMonthInvoices = paidInvoices.filter(i => 
      new Date(i.invoice_date || i.created_at) >= lastMonthStart
    )
    const monthlyRevenue = lastMonthInvoices.reduce((sum, inv) => 
      sum + parseFloat(inv.total_amount?.toString() || '0'), 0
    )

    // Previous month revenue for growth calculation
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)
    const previousMonthInvoices = paidInvoices.filter(i => {
      const invDate = new Date(i.invoice_date || i.created_at)
      return invDate >= twoMonthsAgo && invDate < lastMonthStart
    })
    const previousMonthRevenue = previousMonthInvoices.reduce((sum, inv) => 
      sum + parseFloat(inv.total_amount?.toString() || '0'), 0
    )

    // Monthly growth percentage
    const monthlyGrowth = previousMonthRevenue > 0 
      ? ((monthlyRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 
      : 0

    // Average revenue per tenant
    const avgRevenuePerTenant = activeTenants > 0 ? totalRevenue / activeTenants : 0

    // Active users (from tenant_users if available)
    const activeUsers = tenantUsers?.filter(u => u.status === 'active').length || 0

    // Churn rate calculation (tenants that cancelled in last 30 days)
    const cancelledSubscriptions = subscriptions?.filter(s => 
      s.status === 'cancelled' && 
      s.cancelled_at && 
      new Date(s.cancelled_at) >= lastMonthStart
    ).length || 0
    const churnRate = activeTenants > 0 ? (cancelledSubscriptions / activeTenants) * 100 : 0

    // System uptime (mock for now - would come from monitoring system)
    const systemUptime = 99.9

    // Pending approvals (tenants with pending status)
    const pendingApprovals = tenants?.filter(t => t.status === 'pending').length || 0

    // Support tickets (mock for now - would come from support system)
    const supportTickets = 0

    return {
      totalTenants,
      activeTenants,
      totalRevenue,
      monthlyRevenue,
      monthlyGrowth: parseFloat(monthlyGrowth.toFixed(2)),
      pendingApprovals,
      systemUptime,
      activeSubscriptions,
      trialTenants: trialSubscriptions,
      avgRevenuePerTenant: parseFloat(avgRevenuePerTenant.toFixed(2)),
      supportTickets,
      activeUsers,
      churnRate: parseFloat(churnRate.toFixed(2)),
      newSignupsToday,
      newSignupsThisWeek
    }
  } catch (error) {
    console.error('[getPlatformDashboardMetrics] Error:', error)
    throw new Error(`Failed to fetch platform dashboard metrics: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * ============================================================================
 * MARKETPLACE & ADD-ONS FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches marketplace add-ons
 */
export async function fetchMarketplaceAddons(options?: {
  category?: string
  status?: string
  isFeatured?: boolean
  targetPlan?: string
}) {
  const db = getDb()
  let query = db
    .from('marketplace_addons')
    .select('*')

  if (options?.category) {
    query = query.eq('category', options.category)
  }

  if (options?.status) {
    query = query.eq('status', options.status)
  }

  if (options?.isFeatured !== undefined) {
    query = query.eq('is_featured', options.isFeatured)
  }

  if (options?.targetPlan) {
    query = query.contains('target_plans', [options.targetPlan])
  }

  const { data, error } = await query
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch marketplace addons: ${error.message}`)
  }

  return data || []
}

/**
 * Creates a marketplace add-on
 */
export async function createMarketplaceAddon(addonData: {
  slug: string
  name: string
  description?: string
  long_description?: string
  category: string
  price: number
  billing_period?: string
  features?: any[]
  target_roles?: string[]
  target_plans?: string[]
  status?: string
  is_featured?: boolean
  display_order?: number
}) {
  const db = getDb()
  const { data, error } = await db
    .from('marketplace_addons')
    .insert({
      slug: addonData.slug,
      name: addonData.name,
      description: addonData.description || null,
      long_description: addonData.long_description || null,
      category: addonData.category,
      price: addonData.price,
      billing_period: addonData.billing_period || 'monthly',
      features: addonData.features || [],
      target_roles: addonData.target_roles || [],
      target_plans: addonData.target_plans || [],
      status: addonData.status || 'draft',
      is_featured: addonData.is_featured || false,
      display_order: addonData.display_order || 0
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create marketplace addon: ${error.message}`)
  }

  return data
}

/**
 * Updates a marketplace add-on
 */
export async function updateMarketplaceAddon(
  addonId: string,
  updates: {
    name?: string
    description?: string
    price?: number
    status?: string
    is_featured?: boolean
    [key: string]: any
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  Object.keys(updates).forEach(key => {
    if (updates[key] !== undefined) {
      updateData[key] = updates[key]
    }
  })

  const { data, error } = await db
    .from('marketplace_addons')
    .update(updateData)
    .or(`id.eq.${addonId},slug.eq.${addonId}`)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update marketplace addon: ${error.message}`)
  }

  return data
}

/**
 * Deletes a marketplace add-on
 */
export async function deleteMarketplaceAddon(addonId: string) {
  const db = getDb()
  const { error } = await db
    .from('marketplace_addons')
    .delete()
    .or(`id.eq.${addonId},slug.eq.${addonId}`)

  if (error) {
    throw new Error(`Failed to delete marketplace addon: ${error.message}`)
  }

  return { success: true }
}

/**
 * Installs an add-on for a tenant
 */
export async function installAddonForTenant(tenantId: string, addonId: string, configuration?: any) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_addon_installations')
    .insert({
      tenant_id: tenantId,
      addon_id: addonId,
      status: 'active',
      configuration: configuration || {}
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to install addon: ${error.message}`)
  }

  return data
}

/**
 * ============================================================================
 * VERTICALS FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches all active verticals
 */
export async function fetchVerticals(includeInactive = false) {
  const db = getDb()
  let query = db
    .from('verticals')
    .select('*')
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (!includeInactive) {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Failed to fetch verticals: ${error.message}`)
  }

  return data || []
}

/**
 * Creates a new vertical
 */
export async function createVertical(verticalData: {
  slug: string
  name: string
  description?: string
  icon?: string
  is_active?: boolean
  display_order?: number
  features?: any[]
}) {
  const db = getDb()
  const { data, error } = await db
    .from('verticals')
    .insert({
      slug: verticalData.slug.trim().toLowerCase(),
      name: verticalData.name.trim(),
      description: verticalData.description?.trim() || null,
      icon: verticalData.icon || null,
      is_active: verticalData.is_active !== undefined ? verticalData.is_active : true,
      display_order: verticalData.display_order || 0,
      features: verticalData.features || []
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create vertical: ${error.message}`)
  }

  return data
}

/**
 * Updates a vertical
 */
export async function updateVertical(verticalId: string, updates: {
  name?: string
  description?: string
  icon?: string
  is_active?: boolean
  display_order?: number
  features?: any[]
}) {
  const db = getDb()
  const { data, error } = await db
    .from('verticals')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', verticalId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update vertical: ${error.message}`)
  }

  return data
}

/**
 * Deletes a vertical
 */
export async function deleteVertical(verticalId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('verticals')
    .delete()
    .eq('id', verticalId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to delete vertical: ${error.message}`)
  }

  return data
}

/**
 * ============================================================================
 * SETTINGS FUNCTIONS
 * ============================================================================
 */

/**
 * Gets a setting value
 * IMPORTANT: For tenant-specific settings, tenantId MUST be provided.
 * If tenantId is missing, this will return platform-wide settings (tenant_id IS NULL),
 * which may cause data leakage between tenants.
 */
export async function getSetting(key: string, tenantId?: string) {
  if (!tenantId) {
    console.warn(`[getSetting] Warning: tenantId is missing for key "${key}". This may return platform-wide settings.`)
  }
  
  const db = getDb()
  const { data, error } = await db.rpc('get_setting_value', {
    setting_key_param: key,
    tenant_id_param: tenantId || null
  })

  if (error) {
    throw new Error(`Failed to get setting: ${error.message}`)
  }

  return data
}

/**
 * Gets all settings for a category
 */
export async function getSettingsByCategory(category: string, tenantId?: string) {
  const db = getDb()
  const { data, error } = await db.rpc('get_settings_by_category', {
    category_param: category,
    tenant_id_param: tenantId || null
  })

  if (error) {
    throw new Error(`Failed to get settings: ${error.message}`)
  }

  return data || []
}

/**
 * Sets a setting value
 * IMPORTANT: For tenant-specific settings, tenantId MUST be provided.
 * If tenantId is missing, the setting will be saved as platform-wide (tenant_id IS NULL),
 * which may cause data leakage between tenants.
 */
export async function setSetting(key: string, value: any, tenantId?: string, updatedBy?: string) {
  // Check if this is a tenant-specific setting (from schema)
  const db = getDb()
  
  // Check if setting exists in schema
  const { data: schema, error: schemaError } = await db
    .from('settings_schema')
    .select('*')
    .eq('key', key)
    .single()

  if (schemaError || !schema) {
    const errorMsg = schemaError?.message || 'Setting not found'
    // Provide helpful error message if migration hasn't been run
    if (errorMsg.includes('not found') || errorMsg.includes('No rows')) {
      throw new Error(`Setting key "${key}" not found in schema. Please run migration 026_add_tenant_setup_settings.sql to add tenant setup settings.`)
    }
    throw new Error(`Failed to check setting schema: ${errorMsg}`)
  }

  // If setting is tenant-specific and tenantId is missing, throw error
  if (schema.is_tenant_specific && !tenantId) {
    throw new Error(`Setting "${key}" is tenant-specific but tenantId was not provided. This prevents data leakage between tenants.`)
  }

  if (!tenantId && schema.is_tenant_specific) {
    console.warn(`[setSetting] Warning: Setting "${key}" is tenant-specific but tenantId is missing. This may cause data leakage.`)
  }

  // Upsert setting value
  const { data, error } = await db
    .from('settings_values')
    .upsert({
      setting_key: key,
      tenant_id: tenantId || null,
      value: typeof value === 'string' ? JSON.parse(value) : value,
      updated_by: updatedBy || null
    }, {
      onConflict: 'setting_key,tenant_id'
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to set setting: ${error.message}`)
  }

  return data
}

/**
 * Unified search function
 */
export async function unifiedSearch(query: string, context?: string): Promise<any[]> {
  const db = getDb()
  const results: any[] = []
  const searchTerm = `%${query.toLowerCase()}%`

  // Search tenants
  if (!context || context === 'tenant') {
    const { data: tenants } = await db
      .from('tenants')
      .select('id, slug, name, email')
      .or(`name.ilike.${searchTerm},slug.ilike.${searchTerm},email.ilike.${searchTerm}`)
      .limit(5)

    if (tenants) {
      tenants.forEach(t => {
        results.push({
          id: t.id,
          type: 'tenant',
          title: t.name,
          subtitle: t.slug,
          url: `/tenants/${t.slug}`
        })
      })
    }
  }

  // Search invoices
  if (!context || context === 'invoice') {
    const { data: invoices } = await db
      .from('invoices')
      .select('id, invoice_number, total_amount, status')
      .ilike('invoice_number', searchTerm)
      .limit(5)

    if (invoices) {
      invoices.forEach(i => {
        results.push({
          id: i.id,
          type: 'invoice',
          title: i.invoice_number,
          subtitle: `₹${i.total_amount} - ${i.status}`,
          url: `/billing?invoice=${i.id}`
        })
      })
    }
  }

  // Search plans
  if (!context || context === 'plan') {
    const { data: plans } = await db
      .from('plans')
      .select('id, slug, name')
      .or(`name.ilike.${searchTerm},slug.ilike.${searchTerm}`)
      .limit(5)

    if (plans) {
      plans.forEach(p => {
        results.push({
          id: p.id,
          type: 'plan',
          title: p.name,
          subtitle: p.slug,
          url: `/plans?plan=${p.slug}`
        })
      })
    }
  }

  // Search addons
  if (!context || context === 'addon') {
    const { data: addons } = await db
      .from('marketplace_addons')
      .select('id, slug, name, category')
      .or(`name.ilike.${searchTerm},slug.ilike.${searchTerm}`)
      .limit(5)

    if (addons) {
      addons.forEach(a => {
        results.push({
          id: a.id,
          type: 'addon',
          title: a.name,
          subtitle: a.category,
          url: `/marketplace?addon=${a.slug}`
        })
      })
    }
  }

  // Search templates
  if (!context || context === 'template') {
    const { data: templates } = await db
      .from('catalog_templates')
      .select('id, name, business_category')
      .or(`name.ilike.${searchTerm},business_category.ilike.${searchTerm}`)
      .limit(5)

    if (templates) {
      templates.forEach(t => {
        results.push({
          id: t.id,
          type: 'template',
          title: t.name,
          subtitle: t.business_category,
          url: `/catalog-management?template=${t.id}`
        })
      })
    }
  }

  return results
}

/**
 * ============================================================================
 * BUSINESS CATEGORIES & SEGMENTS FUNCTIONS
 * ============================================================================
 */

/**
 * Fetches all business categories
 */
export async function fetchBusinessCategories(options?: { isActive?: boolean }) {
  const db = getDb()
  let query = db
    .from('business_categories')
    .select('*')

  if (options?.isActive !== undefined) {
    query = query.eq('is_active', options.isActive)
  } else {
    query = query.eq('is_active', true)
  }

  const { data, error } = await query
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch business categories: ${error.message}`)
  }

  return data || []
}

/**
 * Creates a new business category
 */
export async function createBusinessCategory(categoryData: {
  slug: string
  name: string
  description?: string
  icon_name?: string
  display_order?: number
}) {
  const db = getDb()
  const { data, error } = await db
    .from('business_categories')
    .insert({
      slug: categoryData.slug,
      name: categoryData.name,
      description: categoryData.description || null,
      icon_name: categoryData.icon_name || null,
      display_order: categoryData.display_order || 0,
      is_active: true
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create business category: ${error.message}`)
  }

  return data
}

/**
 * Updates a business category
 */
export async function updateBusinessCategory(
  categoryId: string,
  updates: {
    slug?: string
    name?: string
    description?: string
    icon_name?: string
    display_order?: number
    is_active?: boolean
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  Object.keys(updates).forEach(key => {
    if (updates[key as keyof typeof updates] !== undefined) {
      updateData[key] = updates[key as keyof typeof updates]
    }
  })

  const { data, error } = await db
    .from('business_categories')
    .update(updateData)
    .or(`id.eq.${categoryId},slug.eq.${categoryId}`)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update business category: ${error.message}`)
  }

  return data
}

/**
 * Fetches business segments by category
 */
export async function fetchBusinessSegments(categoryIdOrSlug: string) {
  const db = getDb()
  
  // First, try to find category by slug if it's not a UUID
  let categoryId = categoryIdOrSlug
  
  // Check if it's a UUID (UUIDs are 36 characters with dashes)
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(categoryIdOrSlug)
  
  if (!isUUID) {
    // It's likely a slug, find the category
    const { data: categoryData, error: categoryError } = await db
      .from('business_categories')
      .select('id')
      .eq('slug', categoryIdOrSlug)
      .eq('is_active', true)
      .single()
    
    if (!categoryError && categoryData) {
      categoryId = categoryData.id
    } else {
      // If category not found, return empty array
      return []
    }
  }

  // Get segments by category ID
  const { data, error } = await db
    .from('business_segments')
    .select('*')
    .eq('category_id', categoryId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    throw new Error(`Failed to fetch business segments: ${error.message}`)
  }

  return data || []
}

/**
 * Creates a new business segment
 */
export async function createBusinessSegment(segmentData: {
  category_id: string
  name: string
  description?: string
  display_order?: number
}) {
  const db = getDb()
  const { data, error } = await db
    .from('business_segments')
    .insert({
      category_id: segmentData.category_id,
      name: segmentData.name,
      description: segmentData.description || null,
      display_order: segmentData.display_order || 0,
      is_active: true
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create business segment: ${error.message}`)
  }

  return data
}

/**
 * Updates a business segment
 */
export async function updateBusinessSegment(
  segmentId: string,
  updates: {
    name?: string
    description?: string
    display_order?: number
    is_active?: boolean
  }
) {
  const db = getDb()
  const updateData: any = {
    updated_at: new Date().toISOString()
  }

  Object.keys(updates).forEach(key => {
    if (updates[key as keyof typeof updates] !== undefined) {
      updateData[key] = updates[key as keyof typeof updates]
    }
  })

  const { data, error } = await db
    .from('business_segments')
    .update(updateData)
    .eq('id', segmentId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update business segment: ${error.message}`)
  }

  return data
}

/**
 * Deletes a business segment
 */
export async function deleteBusinessSegment(segmentId: string) {
  const db = getDb()
  const { error } = await db
    .from('business_segments')
    .delete()
    .eq('id', segmentId)

  if (error) {
    throw new Error(`Failed to delete business segment: ${error.message}`)
  }

  return { success: true }
}

/**
 * Deletes a business category (and all its segments via CASCADE)
 */
export async function deleteBusinessCategory(categoryId: string) {
  const db = getDb()
  const { error } = await db
    .from('business_categories')
    .delete()
    .or(`id.eq.${categoryId},slug.eq.${categoryId}`)

  if (error) {
    throw new Error(`Failed to delete business category: ${error.message}`)
  }

  return { success: true }
}

/**
 * ============================================================================
 * TENANT SIGNUP REQUEST FUNCTIONS
 * ============================================================================
 */

/**
 * Generates a slug from a business name
 */
function generateBusinessSlug(businessName: string): string {
  return businessName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Checks if a business slug is available
 */
export async function checkBusinessSlugAvailability(slug: string): Promise<boolean> {
  const db = getDb()
  
  // Check in tenants table
  const { data: existingTenant } = await db
    .from('tenants')
    .select('id')
    .eq('slug', slug)
    .limit(1)
    .single()
  
  if (existingTenant) {
    return false
  }
  
  // Check in signup requests table
  const { data: existingRequest } = await db
    .from('tenant_signup_requests')
    .select('id')
    .eq('business_slug', slug)
    .limit(1)
    .single()
  
  if (existingRequest) {
    return false
  }
  
  return true
}

/**
 * Creates a new tenant signup request
 */
export async function createTenantSignupRequest(signupData: {
  business_name: string
  contact_person_name: string
  contact_email: string
  contact_phone: string
  country_code?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  vertical?: string
  business_description?: string
}) {
  const db = getDb()
  
  // Generate slug from business name
  let businessSlug = generateBusinessSlug(signupData.business_name)
  
  // Ensure slug is unique
  let slugAvailable = await checkBusinessSlugAvailability(businessSlug)
  let counter = 1
  const originalSlug = businessSlug
  
  while (!slugAvailable) {
    businessSlug = `${originalSlug}-${counter}`
    slugAvailable = await checkBusinessSlugAvailability(businessSlug)
    counter++
    
    // Safety check to prevent infinite loop
    if (counter > 100) {
      throw new Error('Unable to generate unique business slug. Please try a different business name.')
    }
  }
  
  const { data, error } = await db
    .from('tenant_signup_requests')
    .insert({
      business_name: signupData.business_name.trim(),
      business_slug: businessSlug,
      contact_person_name: signupData.contact_person_name.trim(),
      contact_email: signupData.contact_email.trim().toLowerCase(),
      contact_phone: signupData.contact_phone.trim(),
      country_code: signupData.country_code || null,
      address_line1: signupData.address_line1?.trim() || null,
      address_line2: signupData.address_line2?.trim() || null,
      city: signupData.city?.trim() || null,
      state: signupData.state?.trim() || null,
      postal_code: signupData.postal_code?.trim() || null,
      country: signupData.country?.trim() || null,
      vertical: signupData.vertical || null,
      business_description: signupData.business_description?.trim() || null,
      status: 'pending'
    })
    .select()
    .single()
  
  if (error) {
    throw new Error(`Failed to submit signup request: ${error.message}`)
  }
  
  return data
}

/**
 * Fetches all tenant signup requests (for platform admin)
 */
export async function fetchTenantSignupRequests(options?: {
  status?: 'pending' | 'approved' | 'rejected' | 'onboarding'
}) {
  const db = getDb()
  let query = db
    .from('tenant_signup_requests')
    .select('*')
    .order('created_at', { ascending: false })
  
  if (options?.status) {
    query = query.eq('status', options.status)
  }
  
  const { data, error } = await query
  
  if (error) {
    console.error('[fetchTenantSignupRequests] Fetch error:', error)
    throw new Error(`Failed to fetch signup requests: ${error.message}`)
  }
  
  const requests = data || []
  console.log(`[fetchTenantSignupRequests] Fetched ${requests.length} requests`, {
    filter: options?.status || 'all',
    statuses: requests.map(r => ({ id: r.id, name: r.business_name, status: r.status }))
  })
  
  return requests
}

/**
 * Fetches a single tenant signup request by ID
 */
export async function fetchTenantSignupRequestById(requestId: string) {
  const db = getDb()
  const { data, error } = await db
    .from('tenant_signup_requests')
    .select('*')
    .eq('id', requestId)
    .single()
  
  if (error) {
    throw new Error(`Failed to fetch signup request: ${error.message}`)
  }
  
  return data
}

/**
 * Approves a tenant signup request and creates the tenant
 */
export async function approveTenantSignupRequest(
  requestId: string,
  approvalData: {
    plan?: string
    vertical?: string
    currency?: string
    enabled_languages?: string[]
    default_language?: string
    catalog_template_id?: string | null
    approval_notes?: string
  }
) {
  const db = getDb()
  
  // Fetch the signup request
  const signupRequest = await fetchTenantSignupRequestById(requestId)
  
  if (!signupRequest) {
    throw new Error('Signup request not found')
  }
  
  if (signupRequest.status !== 'pending') {
    throw new Error(`Cannot approve signup request with status: ${signupRequest.status}`)
  }
  
  // Check if tenant already exists (by slug)
  let existingTenant = null
  try {
    existingTenant = await fetchTenantBySlug(signupRequest.business_slug)
  } catch (error) {
    // Tenant doesn't exist, which is fine - we'll create it
    existingTenant = null
  }
  
  if (existingTenant) {
    // Tenant already exists - link the signup request to existing tenant instead of creating new one
    const approvalNotes = approvalData.approval_notes || `Signup request linked to existing tenant "${existingTenant.name}" (slug: ${existingTenant.slug}). Tenant was already created previously.`
    
    console.log('[approveTenantSignupRequest] Linking to existing tenant:', {
      requestId,
      tenantId: existingTenant.id,
      tenantName: existingTenant.name
    })
    
    // Update without .single() to avoid coercion errors
    // Use .select() to get count of affected rows
    const { data: updateData, error: updateError, count } = await db
      .from('tenant_signup_requests')
      .update({
        status: 'approved',
        tenant_id: existingTenant.id,
        approved_at: new Date().toISOString(),
        approval_notes: approvalNotes
      })
      .eq('id', requestId)
      .select()
    
    if (updateError) {
      console.error('[approveTenantSignupRequest] Update error:', updateError)
      throw new Error(`Failed to link signup request to existing tenant: ${updateError.message}`)
    }
    
    // Check if any rows were actually updated
    if (!updateData || updateData.length === 0) {
      console.error('[approveTenantSignupRequest] No rows updated! RLS might be blocking the update.')
      console.error('[approveTenantSignupRequest] Update data:', updateData)
      console.error('[approveTenantSignupRequest] Count:', count)
      throw new Error('Failed to update signup request - no rows were affected. This might be due to RLS policies.')
    }
    
    console.log('[approveTenantSignupRequest] Update successful. Rows affected:', updateData.length)
    
    // Fetch the updated request separately
    const updatedRequest = await fetchTenantSignupRequestById(requestId)
    
    if (!updatedRequest) {
      console.error('[approveTenantSignupRequest] Failed to fetch updated request')
      throw new Error('Failed to fetch updated signup request after linking to existing tenant')
    }
    
    console.log('[approveTenantSignupRequest] Request linked successfully:', {
      id: updatedRequest.id,
      status: updatedRequest.status,
      tenant_id: updatedRequest.tenant_id
    })
    
    // Verify status was actually updated
    if (updatedRequest.status !== 'approved') {
      console.error('[approveTenantSignupRequest] STATUS MISMATCH! Expected: approved, Got:', updatedRequest.status)
      throw new Error(`Status update failed. Expected 'approved' but got '${updatedRequest.status}'`)
    }
    
    return {
      signupRequest: updatedRequest,
      tenant: existingTenant,
      credentials: {
        admin_username: existingTenant.admin_username,
        admin_password: null, // Password not available for existing tenant
        admin_portal_url: existingTenant.admin_portal_url,
        customer_website_url: existingTenant.customer_website_url,
        api_key: existingTenant.api_key
      },
      isExistingTenant: true
    }
  }
  
  // Create the tenant (credentials will be auto-generated)
  const tenantData = {
    slug: signupRequest.business_slug,
    name: signupRequest.business_name,
    email: signupRequest.contact_email,
    phone: signupRequest.contact_phone,
    country_code: signupRequest.country_code || null,
    plan: approvalData.plan || 'starter',
    vertical: approvalData.vertical || signupRequest.vertical || 'other',
    status: 'active',
    currency: approvalData.currency || 'INR',
    enabled_languages: approvalData.enabled_languages || ['en'],
    default_language: approvalData.default_language || 'en',
    catalog_template_id: approvalData.catalog_template_id || null
  }
  
  const tenant = await createTenant(tenantData)
  
  // Extract generated credentials (password is only available right after creation)
  const generatedPassword = (tenant as any)._generated_password
  const adminUsername = tenant.admin_username || `admin_${signupRequest.business_slug}`
  
  // Auto-generate approval notes with credentials
  const approvalNotes = `Tenant approved and created on ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}. Business: ${signupRequest.business_name}. Contact: ${signupRequest.contact_email}. 

Login Credentials:
Username: ${adminUsername}
Password: ${generatedPassword || '[Generated - check tenant record]'}

Admin Portal: ${tenant.admin_portal_url || 'N/A'}
Customer Website: ${tenant.customer_website_url || 'N/A'}

Note: Tenant can select preferred plan and currency during profile onboarding. Please change the password after first login.`
  
  console.log('[approveTenantSignupRequest] Updating request to link to new tenant:', {
    requestId,
    tenantId: tenant.id,
    tenantName: tenant.name
  })
  
  // Update without .single() to avoid coercion errors
  const { data: updateData, error: updateError, count } = await db
    .from('tenant_signup_requests')
    .update({
      status: 'onboarding',
      tenant_id: tenant.id,
      approved_at: new Date().toISOString(),
      approval_notes: approvalData.approval_notes || approvalNotes
    })
    .eq('id', requestId)
    .select()
  
  if (updateError) {
    console.error('[approveTenantSignupRequest] Update error:', updateError)
    throw new Error(`Failed to update signup request: ${updateError.message}`)
  }
  
  // Check if any rows were actually updated
  if (!updateData || updateData.length === 0) {
    console.error('[approveTenantSignupRequest] No rows updated! RLS might be blocking the update.')
    console.error('[approveTenantSignupRequest] Update data:', updateData)
    console.error('[approveTenantSignupRequest] Count:', count)
    throw new Error('Failed to update signup request - no rows were affected. This might be due to RLS policies.')
  }
  
  console.log('[approveTenantSignupRequest] Update successful. Rows affected:', updateData.length)
  
  // Fetch the updated request separately
  const updatedRequest = await fetchTenantSignupRequestById(requestId)
  
  if (!updatedRequest) {
    console.error('[approveTenantSignupRequest] Failed to fetch updated request')
    throw new Error('Failed to fetch updated signup request after approval')
  }
  
  console.log('[approveTenantSignupRequest] Request updated successfully:', {
    id: updatedRequest.id,
    status: updatedRequest.status,
    tenant_id: updatedRequest.tenant_id,
    approved_at: updatedRequest.approved_at
  })
  
  // Verify status was actually updated
  if (updatedRequest.status !== 'onboarding') {
    console.error('[approveTenantSignupRequest] STATUS MISMATCH! Expected: onboarding, Got:', updatedRequest.status)
    throw new Error(`Status update failed. Expected 'onboarding' but got '${updatedRequest.status}'`)
  }
  
  return {
    signupRequest: updatedRequest,
    tenant,
    credentials: {
      admin_username: adminUsername,
      admin_password: generatedPassword || null, // Plain password (only available right after creation)
      admin_portal_url: tenant.admin_portal_url,
      customer_website_url: tenant.customer_website_url,
      api_key: tenant.api_key
    },
    isExistingTenant: false
  }
}

/**
 * Rejects a tenant signup request
 */
export async function rejectTenantSignupRequest(
  requestId: string,
  rejectionNotes?: string
) {
  const db = getDb()
  
  console.log('[rejectTenantSignupRequest] Rejecting request:', requestId)
  
  // Update without .single() to avoid coercion errors
  const { data: updateData, error: updateError, count } = await db
    .from('tenant_signup_requests')
    .update({
      status: 'rejected',
      approved_at: new Date().toISOString(),
      approval_notes: rejectionNotes || null
    })
    .eq('id', requestId)
    .select()
  
  if (updateError) {
    console.error('[rejectTenantSignupRequest] Update error:', updateError)
    throw new Error(`Failed to reject signup request: ${updateError.message}`)
  }
  
  // Check if any rows were actually updated
  if (!updateData || updateData.length === 0) {
    console.error('[rejectTenantSignupRequest] No rows updated! RLS might be blocking the update.')
    console.error('[rejectTenantSignupRequest] Update data:', updateData)
    console.error('[rejectTenantSignupRequest] Count:', count)
    throw new Error('Failed to reject signup request - no rows were affected. This might be due to RLS policies.')
  }
  
  console.log('[rejectTenantSignupRequest] Update successful. Rows affected:', updateData.length)
  
  // Fetch the updated request separately
  const updatedRequest = await fetchTenantSignupRequestById(requestId)
  
  if (!updatedRequest) {
    console.error('[rejectTenantSignupRequest] Failed to fetch updated request')
    throw new Error('Failed to fetch updated signup request after rejection')
  }
  
  console.log('[rejectTenantSignupRequest] Request rejected successfully:', {
    id: updatedRequest.id,
    status: updatedRequest.status
  })
  
  // Verify status was actually updated
  if (updatedRequest.status !== 'rejected') {
    console.error('[rejectTenantSignupRequest] STATUS MISMATCH! Expected: rejected, Got:', updatedRequest.status)
    throw new Error(`Status update failed. Expected 'rejected' but got '${updatedRequest.status}'`)
  }
  
  return updatedRequest
}

/**
 * Deletes a tenant signup request
 */
export async function deleteTenantSignupRequest(requestId: string) {
  const db = getDb()
  
  console.log('[deleteTenantSignupRequest] Attempting to delete request:', requestId)
  
  // First verify the request exists
  const existingRequest = await fetchTenantSignupRequestById(requestId).catch(() => null)
  if (!existingRequest) {
    console.warn('[deleteTenantSignupRequest] Request not found:', requestId)
    throw new Error('Signup request not found')
  }
  
  console.log('[deleteTenantSignupRequest] Request found, deleting:', {
    id: existingRequest.id,
    business_name: existingRequest.business_name,
    status: existingRequest.status
  })
  
  const { data: deleteData, error, count } = await db
    .from('tenant_signup_requests')
    .delete()
    .eq('id', requestId)
    .select()
  
  if (error) {
    console.error('[deleteTenantSignupRequest] Delete error:', error)
    throw new Error(`Failed to delete signup request: ${error.message}`)
  }
  
  // Check if any rows were actually deleted
  if (!deleteData || deleteData.length === 0) {
    console.error('[deleteTenantSignupRequest] No rows deleted! RLS might be blocking the delete.')
    console.error('[deleteTenantSignupRequest] Delete data:', deleteData)
    console.error('[deleteTenantSignupRequest] Count:', count)
    throw new Error('Failed to delete signup request - no rows were affected. This might be due to RLS policies.')
  }
  
  console.log('[deleteTenantSignupRequest] Delete successful. Rows deleted:', deleteData.length)
  
  // Verify deletion
  const verifyRequest = await fetchTenantSignupRequestById(requestId).catch(() => null)
  if (verifyRequest) {
    console.error('[deleteTenantSignupRequest] WARNING: Request still exists after delete!')
    throw new Error('Failed to delete signup request - request still exists')
  }
  
  console.log('[deleteTenantSignupRequest] Deletion verified - request no longer exists')
  
  return { success: true, deletedId: requestId }
}

/**
 * Test function to verify signup requests are in the database
 * This helps debug if data is being saved/retrieved correctly
 */
export async function testSignupRequestsDatabase() {
  const db = getDb()
  
  try {
    // Test 1: Count all requests
    const { count, error: countError } = await db
      .from('tenant_signup_requests')
      .select('*', { count: 'exact', head: true })
    
    console.log('[testSignupRequestsDatabase] Total requests in DB:', count)
    if (countError) {
      console.error('[testSignupRequestsDatabase] Count error:', countError)
    }
    
    // Test 2: Fetch all requests with status breakdown
    const { data: allRequests, error: fetchError } = await db
      .from('tenant_signup_requests')
      .select('id, business_name, status, created_at, approved_at, tenant_id')
      .order('created_at', { ascending: false })
    
    if (fetchError) {
      console.error('[testSignupRequestsDatabase] Fetch error:', fetchError)
      return { success: false, error: fetchError.message }
    }
    
    const statusBreakdown = {
      pending: allRequests?.filter(r => r.status === 'pending').length || 0,
      approved: allRequests?.filter(r => r.status === 'approved').length || 0,
      rejected: allRequests?.filter(r => r.status === 'rejected').length || 0,
      onboarding: allRequests?.filter(r => r.status === 'onboarding').length || 0
    }
    
    console.log('[testSignupRequestsDatabase] Status breakdown:', statusBreakdown)
    console.log('[testSignupRequestsDatabase] All requests:', allRequests?.map(r => ({
      id: r.id,
      name: r.business_name,
      status: r.status,
      created: r.created_at,
      approved: r.approved_at,
      tenant_id: r.tenant_id
    })))
    
    return {
      success: true,
      totalCount: count,
      statusBreakdown,
      requests: allRequests || []
    }
  } catch (error) {
    console.error('[testSignupRequestsDatabase] Error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}